import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const statusLabel = (status) => ({
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  skipped: 'Skipped',
}[status] || status || 'Unknown');

const statusClass = (status) => {
  if (status === 'failed') return 'text-destructive';
  if (status === 'delivered') return 'text-emerald-700';
  if (status === 'sent') return 'text-emerald-700';
  if (status === 'skipped') return 'text-slate-600';
  return 'text-amber-700';
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');

const webhookStatusLabel = (log) => {
  if (log.delivery_status_source === 'webhook') return log.message_status || 'Received';
  if (log.delivery_status_source === 'timeout') return 'Timed out';
  return 'Not received yet';
};

const sourceLabel = (source) => ({
  webhook: 'Webhook',
  timeout: 'Timeout',
}[source] || 'API response');

export default function WhatsAppLogs() {
  const { currentEvent } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (currentEvent?.id) setEventId((value) => value || currentEvent.id);
  }, [currentEvent?.id]);

  useEffect(() => {
    api.get('/events').then((response) => setEvents(response.data || [])).catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    if (!eventId) {
      setLogs([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/whatsapp/logs', { params: { event_id: eventId, limit: 100, ...(status ? { status } : {}) } })
      .then((response) => { if (!cancelled) setLogs(response.data.items || []); })
      .catch(() => { if (!cancelled) { setLogs([]); setError('Could not load WhatsApp activity for this event.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, status, revision]);

  return <DashboardLayout>
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-serif text-3xl font-bold">WhatsApp activity</h1>
        <p className="mt-1 text-sm text-muted-foreground">Delivery status for WhatsApp messages in an event you manage. Personal message details are hidden.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <select aria-label="Event" className="h-9 rounded-md border bg-background px-3 text-sm" value={eventId} onChange={(event) => setEventId(event.target.value)}>
          <option value="">Choose an event</option>
          {events.map((event) => <option key={event.id} value={event.id}>{event.name || event.id}</option>)}
        </select>
        <select aria-label="Delivery status" className="h-9 rounded-md border bg-background px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">All delivery statuses</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>Refresh</Button>
      </div>
      {error && <p role="alert" className="rounded-md border border-destructive/30 p-3 text-destructive">{error}</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
      <div className="space-y-2">
        {logs.map((log, index) => <div key={log.id || index} className="rounded-lg border bg-card p-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-medium">{log.template_name || log.trigger || 'WhatsApp message'}</span>
              {log.recipient_count > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {log.recipient_count} recipient{log.recipient_count === 1 ? '' : 's'}
                </p>
              )}
            </div>
            <span className={statusClass(log.status)}>
              {statusLabel(log.status)}
            </span>
          </div>
          <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            <span>{formatDateTime(log.created_at)}</span>
            <span>Trigger: {log.trigger || '-'}</span>
            <span>Recipients: {(log.recipient_names || []).join(', ') || '-'}</span>
            <span>Message ID: {log.message_id || '-'}</span>
            <span>API response: {log.message_id ? 'accepted' : log.provider_status_code ? `HTTP ${log.provider_status_code}` : '-'}</span>
            <span>Webhook status: {webhookStatusLabel(log)}</span>
            <span>Webhook source: {sourceLabel(log.delivery_status_source)}</span>
            <span>Webhook updated: {formatDateTime(log.delivery_updated_at || log.provider_status_at || log.timed_out_at)}</span>
            <span>Provider code: {log.delivery_error_code || log.provider_error_code || log.provider_error?.code || log.provider_status_code || '-'}</span>
            <span>Reason: {log.failure_reason || '-'}</span>
          </div>
        </div>)}
        {!loading && !logs.length && <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">No WhatsApp attempts for this event and filter.</p>}
      </div>
    </div>
  </DashboardLayout>;
}
